import {
  FilterSelector,
  KeyValue,
} from '@delmonicos/shared-design-system-react/src/components/filter-selector/filter-selector';
import {
  useDebounce,
  usePaginatedFilter,
  usePaginatedFilterControls,
} from '@delmonicos/shared-sdk/libs/core/src/hooks';
import { GatewayChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/outputs/gateway-charge-point.model';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetChargePointsPaginatedQuery } from '@service/cpms-bo-charge-point.service';

const LIMIT = 4;

interface ChargePointFilterControllerProps {
  onChange: (itemIds: string[]) => void;
  selectedItemIds: string[];
}

export const ChargePointFilterController = ({
  onChange,
  selectedItemIds,
}: ChargePointFilterControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const [search, setSearch, offset, incrementOffset, resetOffset] =
    usePaginatedFilterControls(LIMIT);
  const [isTyping, setIsTyping] = useState(false);

  const debouncedSearch = useDebounce(search);

  const { data, isFetching } = useCpmsBoGetChargePointsPaginatedQuery({
    limit: LIMIT,
    offset,
    name: debouncedSearch ? `~${debouncedSearch}` : '~',
  });

  const handleMap = useCallback(
    (chargePoint: GatewayChargePointModel): KeyValue => ({
      key: chargePoint.id,
      value: chargePoint.name,
    }),
    [],
  );

  const { items, updateItems } = usePaginatedFilter<KeyValue>({
    data,
    offset,
    onMap: handleMap,
  });

  useEffect(() => {
    if (debouncedSearch !== undefined) {
      setIsTyping(false);
    }
  }, [debouncedSearch]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <FilterSelector
      items={items}
      isLoading={isFetching || isTyping}
      initialSelectedItemIds={selectedItemIds}
      onChange={onChange}
      onSearchChange={(val) => {
        setSearch(val);
        setIsTyping(true);
        resetOffset();
        updateItems([]);
      }}
      onOffsetChange={incrementOffset}
      labels={{
        title: t('delmonicos-shared-sdk.outputs-transaction.charge-point'),
        search: t('common.filter.search'),
        seeMore: t('common.filter.seeMore'),
      }}
      icon="electric-terminal"
      hasSeeMore={!!data?.hasNextPage}
    />
  );
};
